import { type CountryCode, type ProjectCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { AnalyticsPlatforms } from '../core/createAnalyticsProviders'
import { type FastpassCampaignItem, type ProductCategoryEvent } from '../types/fastpass-campaign-events'
import { useAnalytics } from './useAnalytics'

interface UseFastpassCampaignGoogleAnalyticEventProps {
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignId: number
}

interface ExpandChildProductEventParams {
  sku: string
  realSku: string
  id: number
  name: string
  price: number
  productCategory: string
}

interface ChangeBrandEventParams {
  brandFrom: string
  brandTo: string
}

interface SearchKeywordEventParams {
  searchKeyword: string
}

interface ClickSearchResultEventParams {
  productSku: string
  productRealSku?: string
  variationId: number
  productName: string
}

interface ClickPastResultEventParams {
  pastKeyword: string
}

interface AddToCartEventParams {
  value: number
  currency: string
  items: FastpassCampaignItem[]
  cartUuid: string
}

interface BeginCheckoutEventParams {
  currency: string
  value: number
  campaignCode: string
  items: FastpassCampaignItem[]
}

interface RemoveFromCartEventParams {
  currency: string
  value: number
  items: FastpassCampaignItem[]
}

interface RemoveProductEventParams {
  productSku: string
  productRealSku: string
  variationId: number
  productName: string
  productPrice: number
}

interface GoToPromotionPageEventParams {
  couponTab: string
  couponId: number
  couponTitle: string
}

interface ApplyCouponCodeEventParams {
  couponId: string
  couponTitle: string
  couponThreshold: string
}

export const useFastpassCampaignGoogleAnalyticEvent = (props: UseFastpassCampaignGoogleAnalyticEventProps) => {
  const { projectCode, countryCode, campaignId } = props
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })

  /** campaign 目前沒有 promotion_page 相關 event 先 comment 掉，之後有了再改回來 */
  // const sendPromotionPageEvent = useCallbackEvent((eventName: PromotionPageEvent['eventName']) => {
  //   sendEvent({
  //     eventName,
  //     eventParams: {
  //       event_category: 'promotion_page',
  //       project_code: projectCode,
  //       country_code: countryCode,
  //       campaign_code: campaignId,
  //     },
  //   })
  // })

  /**
   * 點擊商品後下拉展開
   * Business impact: 想知道用戶是否需要更多商品資訊、商品細節是否影響決策
   * @param sku 商品變體 SKU（前端顯示名稱）
   * @param realSku 系統 SKU（真實庫存用）
   * @param id 商品唯一 ID
   * @param name 商品名稱
   * @param price 商品價格
   * @param productCategory 商品分類來源
   */
  const sendExpandChildProductEvent = useCallbackEvent((params: ExpandChildProductEventParams) => {
    const { sku, realSku, id, name, price, productCategory } = params
    sendEvent({
      eventName: 'fastpass_campaign_expand_child_product',
      eventParams: {
        event_category: 'product_click',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        sku,
        real_sku: realSku,
        id,
        name,
        price,
        product_category: productCategory,
      },
    })
  })

  /**
   * 點擊有紅點標示的分類
   * Business impact: 評估「紅點提示」是否有效提升分類點擊率
   * @param eventName 事件名稱
   */
  const sendProductCategoryEvent = useCallbackEvent((eventName: ProductCategoryEvent['eventName']) => {
    sendEvent({
      eventName,
      eventParams: {
        event_category: 'product_categories',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  /**
   * 觀測目前線下展切換品牌下單的比例
   * Business impact: 追蹤跨品牌的切換需求
   * @param brandFrom 切換前的品牌
   * @param brandTo 切換後的品牌
   */
  const sendChangeBrandEvent = useCallbackEvent((params: ChangeBrandEventParams) => {
    const { brandFrom, brandTo } = params
    sendEvent({
      eventName: 'fastpass_campaign_change_brand',
      eventParams: {
        event_category: 'brand_change',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        brand_from: brandFrom,
        brand_to: brandTo,
      },
    })
  })

  /**
   * 用戶點擊搜尋框的比例
   * Business impact: 確認搜尋功能在購物流程中的使用率，是否是主要入口
   */
  const sendClickSearchBarEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_campaign_click_search_bar',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  /**
   * 用戶較常使用關鍵字進行搜尋
   * Business impact: 分析用戶主要搜尋需求與常見關鍵字，優化搜尋體驗與推薦
   * @param searchKeyword 使用者輸入的搜尋關鍵字
   */
  const sendSearchKeywordEvent = useCallbackEvent((params: SearchKeywordEventParams) => {
    const { searchKeyword } = params
    sendEvent({
      eventName: 'fastpass_campaign_search_keyword',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        search_keyword: searchKeyword,
      },
    })
  })

  /**
   * 用戶搜尋後，點擊的品項為何 (購買意願高)
   * Business impact: 確認搜尋結果的相關性與用戶是否能快速找到想要的商品
   * @param productSku 前端商品 SKU
   * @param productRealSku 系統商品 SKU
   * @param variationId 商品變體 ID
   * @param productName 商品名稱
   */
  const sendClickSearchResultEvent = useCallbackEvent((params: ClickSearchResultEventParams) => {
    const { productSku, productRealSku, variationId, productName } = params
    sendEvent({
      eventName: 'fastpass_campaign_click_search_result',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        product_sku: productSku,
        product_real_sku: productRealSku,
        variation_id: variationId,
        product_name: productName,
      },
    })
  })

  /**
   * 用戶會查看過往搜尋的商品 (回購商品)
   * Business impact: 測量回購或重複瀏覽的比例，判斷商品的長期吸引力
   * @param pastKeyword 使用者過往搜尋關鍵字
   */
  const sendClickPastResultEvent = useCallbackEvent((params: ClickPastResultEventParams) => {
    const { pastKeyword } = params
    sendEvent({
      eventName: 'fastpass_campaign_click_past_result',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        past_keyword: pastKeyword,
      },
    })
  })

  /**
   * 加入購物車的比例 / 甚麼商品被加入購物車的比例更高
   * Business impact: 追蹤哪些商品最吸引用戶加入購物車，找出熱門品項與購買傾向
   * @param value 總價值
   * @param currency 幣別
   * @param itemName 商品名稱
   * @param itemId 商品 ID
   * @param itemVariant 商品規格（變體）
   * @param itemBrand 品牌
   * @param quantity 數量
   * @param price 單價
   * @param cartUuid 使用者的購物車唯一識別碼
   */
  const sendAddToCartEvent = useCallbackEvent((params: AddToCartEventParams) => {
    const { value, currency, items, cartUuid } = params
    sendEvent({
      eventName: 'fastpass_campaign_add_to_cart',
      eventParams: {
        event_category: 'add_to_cart',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        value,
        currency,
        items,
        cart_uuid: cartUuid,
      },
    })
  })

  /**
   * 用戶進到購物車頁面
   * Business impact: 測量進入購物車的轉換率，判斷流程是否順暢
   */
  const sendBeginCartEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_campaign_begin_cart',
      eventParams: {
        event_category: 'add_to_cart',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  /**
   * 用戶開始進入 Checkout page
   * Business impact: 衡量購物流程的關鍵轉換點，分析結帳漏斗是否有流失
   * @param currency 幣別
   * @param value 結帳金額
   * @param itemId 商品 ID
   * @param itemName 商品名稱
   * @param itemVariant 商品規格
   * @param itemBrand 品牌
   * @param quantity 數量
   * @param price 單價
   * @param campaignCode 活動代碼
   */
  const sendBeginCheckoutEvent = useCallbackEvent((params: BeginCheckoutEventParams) => {
    const { currency, value, campaignCode, items } = params
    sendEvent({
      eventName: 'fastpass_campaign_begin_checkout',
      eventParams: {
        event_category: 'add_to_cart',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignCode,
        currency,
        value,
        items,
      },
    })
  })

  /**
   * 清空全部購物車的比例
   * Business impact: 觀察用戶放棄購物車的行為，推測價格、促銷或商品選擇是否有問題
   * @param currency 幣別
   * @param value 移除後剩餘金額
   * @param items 移除的商品列表
   */
  const sendRemoveFromCartEvent = useCallbackEvent((params: RemoveFromCartEventParams) => {
    const { currency, value, items } = params
    sendEvent({
      eventName: 'fastpass_campaign_remove_from_cart',
      eventParams: {
        event_category: 'remove_cart',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        currency,
        value,
        items,
      },
    })
  })

  /**
   * 購物車內移除的部分品項的頻率
   * Business impact: 分析用戶對哪些商品有猶豫，找出移除原因與潛在優化機會
   * @param productSku 前端商品 SKU
   * @param productRealSku 系統商品 SKU
   * @param variationId 商品變體 ID
   * @param productName 商品名稱
   * @param productPrice 商品價格
   */
  const sendRemoveProductEvent = useCallbackEvent((params: RemoveProductEventParams) => {
    const { productSku, productRealSku, variationId, productName, productPrice } = params
    sendEvent({
      eventName: 'fastpass_campaign_remove_product',
      eventParams: {
        event_category: 'remove_cart',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        product_sku: productSku,
        product_real_sku: productRealSku,
        variation_id: variationId,
        product_name: productName,
        product_price: productPrice,
      },
    })
  })

  /**
   * 用戶點擊「查看或使用優惠」的比例
   * Business impact: 評估優惠資訊是否具有吸引力，對轉換率的影響程度
   */
  const sendClickCouponEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_campaign_click_coupon',
      eventParams: {
        event_category: 'click_coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  /**
   * 使用者在「優惠彈窗」中點擊促銷活動，並跳轉至對應的優惠商品
   * Business impact: 紀錄用戶從優惠彈窗進一步查看促銷活動頁的行為，還是從首頁按下商品比較多
   * @param couponTab 使用者點擊的優惠分頁
   * @param couponId 優惠代碼
   * @param couponTitle 優惠名稱
   */
  const sendGoToPromotionPageEvent = useCallbackEvent((params: GoToPromotionPageEventParams) => {
    const { couponTab, couponId, couponTitle } = params
    sendEvent({
      eventName: 'fastpass_campaign_go_to_promotion_page',
      eventParams: {
        event_category: 'click_coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        coupon_tab: couponTab,
        coupon_id: couponId,
        coupon_title: couponTitle,
      },
    })
  })

  /**
   * 篩選 coupon 券的使用情況
   * Business impact: 分析用戶如何過濾優惠，了解需求偏好
   */
  const sendPromotionFilterEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_campaign_promotion_filter',
      eventParams: {
        event_category: 'filter_coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  /**
   * 離開優惠券彈窗的比例
   * Business impact: 測量優惠彈窗的干擾程度與吸引力，判斷設計是否需要優化
   */
  const sendLeaveCouponEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_campaign_leave_coupon',
      eventParams: {
        event_category: 'leave_coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  /**
   * 使用「優惠碼」的用戶，輸入的折價券代碼
   * Business impact: 追蹤優惠碼的實際使用情況，驗證活動成效
   * @param couponId 優惠代碼（避免溝通誤會用個中文）
   * @param couponTitle 優惠名稱
   * @param couponThreshold 優惠門檻名稱
   */
  const sendApplyCouponCodeEvent = useCallbackEvent((params: ApplyCouponCodeEventParams) => {
    const { couponId, couponTitle, couponThreshold } = params
    sendEvent({
      eventName: 'fastpass_campaign_apply_coupon_code',
      eventParams: {
        event_category: 'apply_coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        coupon_id: couponId,
        coupon_title: couponTitle,
        coupon_threshold: couponThreshold,
      },
    })
  })

  return {
    sendExpandChildProductEvent,
    sendProductCategoryEvent,
    sendChangeBrandEvent,
    sendClickSearchBarEvent,
    sendSearchKeywordEvent,
    sendClickSearchResultEvent,
    sendClickPastResultEvent,
    sendAddToCartEvent,
    sendBeginCartEvent,
    sendBeginCheckoutEvent,
    sendRemoveFromCartEvent,
    sendRemoveProductEvent,
    sendClickCouponEvent,
    sendGoToPromotionPageEvent,
    sendPromotionFilterEvent,
    sendLeaveCouponEvent,
    sendApplyCouponCodeEvent,
  }
}
